<script setup lang="ts">
import { useCBModelOptions } from '@/composables/rc/control-board-model-options'
import { useFanModelOptions } from '@/composables/rc/fan-model-options'
import { useHashboardModelOptions } from '@/composables/rc/hashboard-model-options'
import { usePSUModelOptions } from '@/composables/rc/power-supply-model-options'
import type { IModel } from '@/types/rc/miners'
import { PartType } from '@/types/rc/miners/PartType'
import { computed, ref, watch } from 'vue'

const selectedModel = defineModel<IModel | undefined>()

const props = defineProps<{ partType?: PartType }>()

const isPSU = computed(() => props.partType === PartType.PowerSupply)
const isHashboard = computed(() => props.partType === PartType.Hashboard)
const isCB = computed(() => props.partType === PartType.ControlBoard)
const isFan = computed(() => props.partType === PartType.Fan)

// Managed input fields
const textModel = ref(selectedModel.value?.name ?? '')
const optionModel = ref(selectedModel.value)

// Sync selectedModel with either dropdown or text managed input
watch([textModel, () => optionModel.value], () => {
  if (isPSU.value || isHashboard.value || isCB.value || isFan.value) selectedModel.value = optionModel.value
  if (optionModel.value === undefined && textModel.value == '') selectedModel.value = undefined
})

// Reset local buffers when model is cleared out
watch(selectedModel, () => {
  if (selectedModel.value?.name == undefined) {
    textModel.value = ''
    optionModel.value = undefined
  }
})

// Clear the managed input fields if the model is cleared
const onClear = () => {
  optionModel.value = undefined
  textModel.value = ''
}

const placeholderText = computed(() => {
  if (isCB.value) return 'Select CB Model'
  if (isPSU.value) return 'Select PSU Model'
  if (isHashboard.value) return 'Select Hashboard Model'
  if (isFan.value) return 'Select Fan Model'
  return 'Enter Model'
})

// Tell which options should go in the dropdown
const modelOptions = computed(() => {
  if (isCB.value && cbModelOptions.value) return cbModelOptions.value
  if (isPSU.value && psuModelOptions.value) return psuModelOptions.value
  if (isHashboard.value && hbModelOptions.value) return hbModelOptions.value
  if (isFan.value && fanModelOptions.value) return fanModelOptions.value
  return []
})

// Pull BE options for model dropdown
const { data: psuModelOptions, isFetching: psuModelsQueryPending } = usePSUModelOptions({ enabled: isPSU })
const { data: hbModelOptions, isFetching: hbModelsQueryPending } = useHashboardModelOptions({ enabled: isHashboard })
const { data: cbModelOptions, isFetching: cbModelsQueryPending } = useCBModelOptions({ enabled: isCB })
const { data: fanModelOptions } = useFanModelOptions({ enabled: isFan })

// Expose
defineExpose({ clear: onClear })
</script>

<template>
  <el-select-v2
    v-model="optionModel"
    :options="modelOptions"
    :placeholder="placeholderText"
    value-key="valueKey"
    filterable
    clearable
    :loading="psuModelsQueryPending || hbModelsQueryPending || cbModelsQueryPending"
    @clear="onClear"
  >
    <template #label="{ value }">
      <span>{{ value.name }} {{ value.version }}</span>
    </template>
  </el-select-v2>
</template>
